import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { TrendingUp, Loader2, X } from 'lucide-react';
import api from '../../services/api';

const signalColors = {
  'Viewed Horse': 'bg-blue-100 text-blue-700',
  'Associate Viewed': 'bg-purple-100 text-purple-700',
  'Repeat View': 'bg-indigo-100 text-indigo-700',
  'Repeat View (Narrowing)': 'bg-indigo-200 text-indigo-800',
  'Interest in Notes': 'bg-yellow-100 text-yellow-700',
  'Liked Horse': 'bg-red-100 text-red-700',
  'Viewed Vet Report': 'bg-blue-100 text-blue-700',
  'Session Day View': 'bg-green-100 text-green-700',
  'Vetting Requested': 'bg-emerald-100 text-emerald-700',
  'Associated Vet Procedure': 'bg-teal-100 text-teal-700',
};

/** Stable key for a single insight — immune to ordering changes */
const insightKey = (insight) =>
  `${insight.sale_opportunity_id || ''}__${insight.viewer_name || ''}`;

/** localStorage helpers scoped to a saleId */
const storageKey = (saleId) => `funnel_dismissed_${saleId}`;

const loadDismissed = (saleId) => {
  try {
    const raw = localStorage.getItem(storageKey(saleId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
};

const saveDismissed = (saleId, set) => {
  try {
    localStorage.setItem(storageKey(saleId), JSON.stringify([...set]));
  } catch { /* quota / private-mode — dismiss still works in-session */ }
};

const InsightDetailDialog = ({ insight, onClose }) => (
  <Dialog open={!!insight} onOpenChange={(v) => { if (!v) onClose(); }}>
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>
          Funnel Insight: #{insight?.hip_number || 'TBD'} {insight?.horse_name}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Viewer:</span>
          <span className="text-sm font-semibold">{insight?.viewer_name}</span>
        </div>
        <div className="space-y-3">
          {insight?.signals.map((s, j) => (
            <div key={j} className="p-3 border rounded space-y-1">
              <div className="flex items-center gap-2">
                <Badge className={`text-xs py-0 px-1.5 ${signalColors[s.signal] || 'bg-gray-100 text-gray-700'}`}>
                  {s.signal}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{s.detail}</p>
              {s.dates && s.dates.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  {s.dates.map((d, k) => <span key={k} className="block">View {k + 1}: {d}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const FunnelInsightsTable = ({ saleId, activeOnly = true }) => {
  const [dismissed, setDismissed] = useState(() => loadDismissed(saleId));
  const [selectedInsight, setSelectedInsight] = useState(null);

  const { data: insights = [], isLoading: loading } = useQuery({
    queryKey: QK.funnelInsights(saleId, activeOnly),
    queryFn: () => api.get(`/sale-cards/funnel-insights?sale_id=${saleId}&active_only=${activeOnly}`).then(r => r.data),
    enabled: !!saleId,
  });

  const dismiss = (insight) => {
    const key = insightKey(insight);
    setDismissed(prev => {
      const next = new Set([...prev, key]);
      saveDismissed(saleId, next);
      return next;
    });
  };

  const visibleInsights = insights.filter(insight => !dismissed.has(insightKey(insight)));

  if (loading) {
    return (
      <Card className="border-2 border-border mt-4">
        <CardContent className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></CardContent>
      </Card>
    );
  }

  if (visibleInsights.length === 0) return null;

  return (
    <>
      <Card className="border-2 border-border mt-4">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Funnel Insights</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3 pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hip #</TableHead>
                <TableHead>Horse</TableHead>
                <TableHead>Viewer</TableHead>
                <TableHead>Signals</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleInsights.map((insight, i) => (
                <TableRow key={insightKey(insight)} data-testid={`funnel-insight-row-${i}`} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedInsight(insight)}>
                  <TableCell className="text-sm font-medium">#{insight.hip_number || 'TBD'}</TableCell>
                  <TableCell className="text-sm">{insight.horse_name || '-'}</TableCell>
                  <TableCell className="text-sm font-medium">{insight.viewer_name}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {insight.signals.map((s, j) => (
                        <Badge key={j} className={`text-xs py-0 px-1.5 ${signalColors[s.signal] || 'bg-gray-100 text-gray-700'}`}>
                          {s.signal}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      data-testid={`dismiss-insight-${i}`}
                      variant="ghost" size="sm"
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-red-500"
                      onClick={(e) => { e.stopPropagation(); dismiss(insight); }}
                    >
                      Dismiss
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InsightDetailDialog insight={selectedInsight} onClose={() => setSelectedInsight(null)} />
    </>
  );
};

// Alert component for Sale Opportunity Activity tab
export const FunnelInsightAlert = ({ opportunityId, saleId }) => {
  const [dismissed, setDismissed] = useState(() => loadDismissed(saleId));
  const [selectedInsight, setSelectedInsight] = useState(null);

  const { data: allInsights = [] } = useQuery({
    queryKey: QK.funnelInsights(saleId, false),
    queryFn: () => api.get(`/sale-cards/funnel-insights?sale_id=${saleId}`).then(r => r.data),
    enabled: !!saleId,
  });
  const insights = allInsights.filter(i => i.sale_opportunity_id === opportunityId);

  const dismiss = (insight) => {
    const key = insightKey(insight);
    setDismissed(prev => {
      const next = new Set([...prev, key]);
      saveDismissed(saleId, next);
      return next;
    });
  };

  const visibleInsights = insights.filter(insight => !dismissed.has(insightKey(insight)));
  if (visibleInsights.length === 0) return null;

  return (
    <>
      <Card className="border border-primary bg-green-50/30 mb-4">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Funnel Insights</span>
          </div>
          <div className="space-y-1">
            {visibleInsights.map((insight, i) => (
              <div key={insightKey(insight)} data-testid={`funnel-alert-row-${i}`} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50">
                <div className="flex items-center gap-2 flex-1 cursor-pointer" onClick={() => setSelectedInsight(insight)}>
                  <span className="text-sm font-medium">{insight.viewer_name}</span>
                  <div className="flex flex-wrap gap-1">
                    {insight.signals.map((s, j) => (
                      <Badge key={j} className={`text-xs py-0 px-1.5 ${signalColors[s.signal] || 'bg-gray-100 text-gray-700'}`}>
                        {s.signal}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button
                  data-testid={`dismiss-alert-${i}`}
                  variant="ghost" size="sm"
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-red-500 shrink-0"
                  onClick={() => dismiss(insight)}
                >
                  Dismiss
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <InsightDetailDialog insight={selectedInsight} onClose={() => setSelectedInsight(null)} />
    </>
  );
};

export default FunnelInsightsTable;
